import { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, InputAdornment, Card, CardContent, CircularProgress, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as DiscountIcon } from '../assets/images/coupon.svg';
import { validateCoupon, getActiveCoupons } from '../api/app';

const ARWebCoupon = ({
  restaurantId,
  orderType,
  cartItems,
  onCouponValidated,
  themeColors,
  paymentGateway
}) => {
  const [couponCode, setCouponCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [showCoupons, setShowCoupons] = useState(false);
  const [isFetchingCoupons, setIsFetchingCoupons] = useState(false);

  const {
    checkoutCardsBgColor = null,
    checkoutCardsFontColor = null,
    checkoutPaymentBtnBgColor = null,
    checkoutPaymentBtnFontColor = null,
  } = themeColors || {};

  const handleCouponChange = (e) => {
    setCouponCode(e.target.value);
    setCouponError('');
    setCouponSuccess('');
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponError('');
    setCouponSuccess('');
    onCouponValidated(null);
  };

  useEffect(() => {
    return () => onCouponValidated(null);
  }, []);

  const handleViewCoupons = async () => {
    if (showCoupons) {
      setShowCoupons(false);
      return;
    }

    setIsFetchingCoupons(true);
    try {
      const response = await getActiveCoupons(restaurantId, orderType);
      if (response?.success && Array.isArray(response.data?.rows)) {
        setAvailableCoupons(response.data.rows);
      } else {
        setAvailableCoupons([]);
      }
      setShowCoupons(true);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      setAvailableCoupons([]);
      setShowCoupons(true);
    } finally {
      setIsFetchingCoupons(false);
    }
  };

  const buildCartItems = () => {
    return cartItems.map(order => {
      let options = [];
      if (order?.selectedOptions) {
        order?.selectedOptions.forEach(opt => {
          if (opt.length) {
            options.push(opt);
          }
        });
      }

      let selectedModifiers = order?.selectedModifiers ?? [];
      const modifiers = selectedModifiers.map(modifier => {
        const menuPrice = modifier?.price ?? null;
        return {
          _idModifiers: modifier._idModifier,
          _idMenuItem: modifier._idMenuItem,
          withExtra: "with",
          price: menuPrice,
          quantity: 1,
        };
      });

      return {
        _idMenuItem: order._id,
        menuItemName: order.name,
        _idCategory: order._idCategory ?? null,
        price: {
          value: order.price.value,
          currency: order.price.currency,
        },
        notes: order.note,
        options: JSON.stringify(options),
        modifiers: modifiers,
        quantity: order.count,
      };
    });
  };

  const validateCouponCode = async (code) => {
    const codeToApply = (code || couponCode).trim();
    if (!codeToApply) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setIsValidating(true);
    setCouponError('');
    setCouponSuccess('');

    try {
      const data = {
        _idRestaurant: restaurantId,
        couponCode: codeToApply,
        orderType: orderType,
        items: buildCartItems(),
      };

      const response = await validateCoupon(JSON.stringify(data));

      if (response.success) {
        setCouponSuccess('Coupon applied successfully!');
        setCouponCode(codeToApply);
        setShowCoupons(false);
        onCouponValidated({ ...response.data, couponName: codeToApply });
      } else {
        setCouponError(response.msg?.split(':')?.[0] || 'Invalid coupon code');
        onCouponValidated(null);
      }
    } catch (error) {
      setCouponError('Error validating coupon');
      onCouponValidated(null);
      console.error('Coupon validation error:', error);
    } finally {
      setIsValidating(false);
    }
  };

  const formatMoney = (money) => {
    if (!money) return null;
    return `${money.currency} ${money.value}`;
  };

  return (
    <Box
      sx={{
        backgroundColor: checkoutCardsBgColor ?? '#FFFFFF',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px',
        marginX: '16px',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography
          sx={{
            color: `${checkoutCardsFontColor ?? "#000"} !important`,
            fontFamily: 'Inter !important',
            fontSize: '16px !important',
            fontWeight: '600 !important',
            marginBottom: '8px'
          }}
        >
          Add Coupon
        </Typography>
        <Box>
          <Button
            variant="text"
            size="small"
            onClick={handleViewCoupons}
            disabled={isFetchingCoupons}
            sx={{
              color: checkoutPaymentBtnBgColor ?? '#212121',
              textTransform: 'none',
              fontSize: '13px',
              fontFamily: 'Inter',
              padding: '0 4px',
              minWidth: 'unset',
            }}
          >
            {isFetchingCoupons ? (
              <CircularProgress size={14} sx={{ mr: 0.5 }} />
            ) : null}
            {showCoupons ? 'Hide Coupons' : 'View Available Coupons'}
          </Button>
        </Box>
      </Box>

      <Box id="available-coupons">
        {showCoupons && (
          <Box sx={{ mb: 2 }}>
            {availableCoupons.length === 0 ? (
              <Typography sx={{ fontSize: '13px', color: checkoutCardsFontColor ?? '#666', fontFamily: 'Inter' }}>
                No coupons available.
              </Typography>
            ) : (
              availableCoupons.map((coupon) => (
                <Card
                  key={coupon._id}
                  variant="outlined"
                  sx={{
                    mb: 1,
                    borderRadius: '8px',
                    opacity: coupon.isAvailable === false ? 0.6 : 1,
                    backgroundColor: checkoutCardsBgColor ?? '#FFFFFF',
                  }}
                >
                  <CardContent sx={{ padding: '10px 12px !important' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1, mr: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 700,
                            fontSize: '14px',
                            color: checkoutCardsFontColor ?? '#000',
                            letterSpacing: '0.5px',
                          }}
                        >
                          {coupon.code}
                        </Typography>
                        {coupon.description && (
                          <Typography
                            sx={{
                              fontFamily: 'Inter',
                              fontSize: '12px',
                              color: checkoutCardsFontColor ?? '#555',
                              mt: 0.25,
                            }}
                          >
                            {coupon.description}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.5 }}>
                          {coupon.minPurchase && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: checkoutCardsFontColor ?? '#777', flexShrink: 0 }} />
                              <Typography sx={{ fontFamily: 'Inter', fontSize: '11px', color: checkoutCardsFontColor ?? '#777' }}>
                                Min spend: {formatMoney(coupon.minPurchase)}
                              </Typography>
                            </Box>
                          )}
                          {coupon.maxDiscount && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: checkoutCardsFontColor ?? '#777', flexShrink: 0 }} />
                              <Typography sx={{ fontFamily: 'Inter', fontSize: '11px', color: checkoutCardsFontColor ?? '#777' }}>
                                Max discount: {formatMoney(coupon.maxDiscount)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        {coupon.isAvailable === false && coupon.availabilityMessage && (
                          <Typography sx={{ fontFamily: 'Inter', fontSize: '11px', color: 'warning.main', mt: 0.5 }}>
                            {coupon.availabilityMessage}
                          </Typography>
                        )}
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={isValidating || coupon.isAvailable === false}
                        onClick={() => validateCouponCode(coupon.code)}
                        sx={{
                          backgroundColor: checkoutPaymentBtnBgColor ?? '#212121',
                          color: checkoutPaymentBtnFontColor ?? '#FFF',
                          '&:hover': {
                            backgroundColor: `${checkoutPaymentBtnBgColor ?? '#212121'} !important`,
                          },
                          textTransform: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          minWidth: '56px',
                          height: '30px',
                          flexShrink: 0,
                        }}
                      >
                        Apply
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Enter coupon code"
          size="small"
          value={couponCode}
          onChange={handleCouponChange}
          disabled={isValidating}
          error={!!couponError}
          helperText={couponError || couponSuccess}
          FormHelperTextProps={{
            sx: {
              color: couponError ? 'error.main' : (couponSuccess ? 'success.main' : 'inherit'),
              marginLeft: 0
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <DiscountIcon width={20} height={20} />
              </InputAdornment>
            ),
            endAdornment: couponSuccess ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleRemoveCoupon} edge="end">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: {
              "& .MuiOutlinedInput-input": {
                color: checkoutCardsFontColor ?? "#000",
              },
              borderRadius: '8px',
            }
          }}
          sx={{ mr: 1 }}
        />
        <Button
          variant="contained"
          onClick={() => validateCouponCode()}
          disabled={isValidating || !couponCode.trim()}
          sx={{
            minWidth: '80px',
            height: '40px',
            backgroundColor: checkoutPaymentBtnBgColor ?? "#212121",
            color: checkoutPaymentBtnFontColor ?? "#FFF",
            '&:hover': {
              backgroundColor: `${checkoutPaymentBtnBgColor ?? "#212121"} !important`,
            },
            textTransform: 'none',
            borderRadius: '8px',
          }}
        >
          Apply
        </Button>
      </Box>
    </Box>
  );
};

export default ARWebCoupon;
